/* Watch Corner — theme scripts */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const routes = window.theme.routes;

  const store = {
    get(key, fallback) {
      try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (e) { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
    }
  };

  function formatMoney(cents) {
    const format = window.theme.moneyFormat || '₹{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withCommas = (sep) => int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withCommas(',');
        case 'amount_with_comma_separator': return withCommas('.') + ',' + dec;
        case 'amount_no_decimals_with_comma_separator': return withCommas('.');
        default: return withCommas(',') + '.' + dec;
      }
    });
  }

  let toastTimer;
  function toast(message) {
    const el = $('#Toast');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  }

  /* ---------- Smooth scrolling (Lenis) ---------- */
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = window.Shopify && window.Shopify.designMode;
  let lenis = null;
  if (window.Lenis && !reducedMotion && !inEditor) {
    lenis = new window.Lenis({ lerp: 0.08, smoothWheel: true, wheelMultiplier: 0.85, touchMultiplier: 1 });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || a.getAttribute('href').length < 2) return;
      const target = document.querySelector(a.getAttribute('href'));
      if (target) { e.preventDefault(); lenis.scrollTo(target, { offset: -90 }); }
    });
  }

  /* ---------- Overlays (drawers, menu, modal, search) ---------- */
  let lastFocus = null;
  function openLayer(el) {
    if (!el) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    if (lenis) lenis.stop();
    const focusable = el.querySelector('input[type="search"], button, a[href]');
    if (focusable) setTimeout(() => focusable.focus(), 50);
  }
  function closeLayer(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (!$('.is-open[aria-hidden="false"]')) {
      document.body.classList.remove('is-locked');
      if (lenis) lenis.start();
    }
    if (lastFocus) lastFocus.focus();
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') $$('.is-open[aria-hidden="false"]').forEach(closeLayer);
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-menu-open]')) return openLayer($('#MobileMenu'));
    if (t.closest('[data-menu-close]')) return closeLayer($('#MobileMenu'));
    if (t.closest('[data-search-open]')) { e.preventDefault(); return openLayer($('#SearchOverlay')); }
    if (t.closest('[data-search-close]')) return closeLayer($('#SearchOverlay'));
    if (t.closest('[data-drawer-close]')) return closeLayer($('#CartDrawer'));
    if (t.closest('[data-modal-close]')) return closeLayer($('#QuickView'));
    if (t.closest('[data-filters-open]')) return openLayer($('#Filters'));
    if (t.closest('[data-filters-close]')) return closeLayer($('#Filters'));
    const cartOpen = t.closest('[data-cart-open]');
    if (cartOpen && $('#CartDrawer') && document.body.dataset.cartType !== 'page') {
      e.preventDefault();
      openLayer($('#CartDrawer'));
    }
  });

  /* ---------- Header scroll behaviour ---------- */
  const header = $('[data-header]');
  if (header) {
    let ticking = false;
    const update = () => { header.classList.toggle('is-scrolled', window.scrollY > 40); ticking = false; };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- Announcement rotator ---------- */
  $$('[data-announcement]').forEach((bar) => {
    const items = $$('.announcement__item', bar);
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      const current = items[i];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      setTimeout(() => current.classList.remove('is-leaving'), 500);
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, Number(bar.dataset.interval) || 4000);
  });

  /* ---------- Scroll reveal ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            el.classList.add('is-visible');
            revealObserver.unobserve(el);
            // Drop stagger delay once shown so hover transitions respond instantly
            setTimeout(() => { el.classList.add('is-done'); el.style.transitionDelay = ''; }, 1400);
          }
        });
      }, { rootMargin: '0px 0px -60px 0px' })
    : null;
  function observeReveal(ctx = document) {
    $$('.reveal:not(.is-visible)', ctx).forEach((el, idx) => {
      el.style.transitionDelay = `${(idx % 6) * 80}ms`;
      if (revealObserver) revealObserver.observe(el); else el.classList.add('is-visible');
    });
  }
  observeReveal();

  /* ---------- Image fade-in ---------- */
  function fadeImages(ctx = document) {
    $$('.card__media img:not(.card__img--hover), .cat-tile__media img, .brand-card__logo img, .brand-tile__media img, .iwt__media img, .gallery__slide img, .article-card__media img', ctx).forEach((img) => {
      if (img.dataset.fade) return;
      img.dataset.fade = '1';
      if (img.complete && img.naturalWidth) return;
      img.classList.add('img-fade');
      const done = () => img.classList.add('is-loaded');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }
  fadeImages();

  /* ---------- Marquee: continuous sliding by default, pause on hover ---------- */
  function initMarquees(ctx = document) {
    $$('[data-marquee]', ctx).forEach((m) => {
      if (m.dataset.marqueeReady) return;
      m.dataset.marqueeReady = '1';
      const track = $('.marquee__track', m);
      const group = $('.marquee__group', m);
      if (!track || !group) return;

      const updateSpeed = () => {
        const speed = Number(m.dataset.speed) || 65; // pixels per second
        const width = group.offsetWidth || 1200;
        const duration = Math.max(6, width / speed);
        track.style.setProperty('--speed', duration.toFixed(2) + 's');
        track.style.setProperty('--marquee-duration', duration.toFixed(2) + 's');
      };

      updateSpeed();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(updateSpeed);
      window.addEventListener('resize', updateSpeed, { passive: true });

      // Explicit hover & touch pause controls
      m.addEventListener('mouseenter', () => { track.style.animationPlayState = 'paused'; });
      m.addEventListener('mouseleave', () => { track.style.animationPlayState = 'running'; });
      m.addEventListener('touchstart', () => { track.style.animationPlayState = 'paused'; }, { passive: true });
      m.addEventListener('touchend', () => { track.style.animationPlayState = 'running'; }, { passive: true });
    });
  }
  initMarquees();

  /* ---------- Slideshow ---------- */
  $$('[data-slideshow]').forEach((root) => {
    const slides = $$('.slide', root);
    const dots = $$('[data-slide-to]', root);
    if (slides.length < 2) return;
    let index = 0;
    let timer;
    const go = (n) => {
      slides[index].classList.remove('is-active');
      dots[index] && dots[index].classList.remove('is-active');
      index = (n + slides.length) % slides.length;
      slides[index].classList.add('is-active');
      if (dots[index]) {
        void dots[index].offsetWidth; // restart progress animation
        dots[index].classList.add('is-active');
      }
    };
    const play = () => {
      if (root.dataset.autoplay !== 'true') return;
      clearInterval(timer);
      timer = setInterval(() => go(index + 1), Number(root.dataset.interval) || 5000);
    };
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-slide-prev]')) { go(index - 1); play(); }
      if (e.target.closest('[data-slide-next]')) { go(index + 1); play(); }
      const dot = e.target.closest('[data-slide-to]');
      if (dot) { go(Number(dot.dataset.slideTo)); play(); }
    });
    let startX = null;
    root.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) { go(index + (dx < 0 ? 1 : -1)); play(); }
      startX = null;
    });
    root.addEventListener('mouseenter', () => { clearInterval(timer); root.classList.add('is-paused'); });
    root.addEventListener('mouseleave', () => { root.classList.remove('is-paused'); play(); });
    play();
  });

  /* ---------- Carousels ---------- */
  $$('[data-carousel]').forEach((root) => {
    const track = $('[data-carousel-track]', root);
    const step = () => {
      const slide = track.firstElementChild;
      return slide ? slide.getBoundingClientRect().width + 20 : track.clientWidth;
    };
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-carousel-prev]')) track.scrollBy({ left: -step(), behavior: 'smooth' });
      if (e.target.closest('[data-carousel-next]')) track.scrollBy({ left: step(), behavior: 'smooth' });
    });
  });

  /* ---------- Brand slider (pager shows current position / positions) ---------- */
  function initBrandSliders(ctx = document) {
    $$('[data-brand-slider]', ctx).forEach((root) => {
      const track = $('[data-brand-track]', root);
      const pager = $('[data-brand-pager]', root);
      if (!track || !pager) return;
      const items = () => Array.from(track.children);
      const prev = $('[data-brand-prev]', root);
      const next = $('[data-brand-next]', root);
      const current = $('[data-brand-current]', root);
      const total = $('[data-brand-total]', root);
      const stepSize = () => {
        const first = items()[0];
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const count = items().length;
        const step = stepSize();
        const visible = Math.max(1, Math.round((track.clientWidth + 1) / step));
        const positions = Math.max(1, count - visible + 1); // number of distinct scroll positions
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        const firstIndex = atEnd ? positions - 1 : Math.round(track.scrollLeft / step);
        total.textContent = positions;
        current.textContent = Math.min(positions, firstIndex + 1);
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        pager.hidden = count <= visible;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: 'smooth' }));
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }
  initBrandSliders();

  /* Live item counts for brands that have no matching collection */
  function loadVendorCounts(ctx = document) {
    $$('[data-vendor-count]', ctx).forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = '1';
      try {
        const url = el.dataset.vendorCount + (el.dataset.vendorCount.includes('?') ? '&' : '?') + 'view=count';
        const n = parseInt(await (await fetch(url)).text(), 10);
        if (Number.isNaN(n)) return;
        const tpl = n === 1 ? window.theme.strings.itemsOne : window.theme.strings.itemsOther;
        el.textContent = tpl.replace('__N__', n);
      } catch (e) { /* leave blank */ }
    });
  }
  loadVendorCounts();

  /* ---------- Tabs ---------- */
  $$('[data-tabs]').forEach((root) => {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-tab]');
      if (!btn) return;
      $$('[data-tab]', root).forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', String(active));
      });
      $$('[data-tab-panel]', root).forEach((p) => p.classList.toggle('is-active', p.dataset.tabPanel === btn.dataset.tab));
    });
  });

  /* ---------- Cart ---------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
  }

  async function refreshCartDrawer(open = true) {
    const res = await fetch(`${routes.root}?sections=cart-drawer`);
    const data = await res.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = $('[data-cart-drawer-inner]', html);
    const current = $('[data-cart-drawer-inner]');
    if (fresh && current) current.replaceWith(fresh);
    const cart = await (await fetch(`${routes.cart}.js`)).json();
    updateCartCount(cart.item_count);
    if (open) openLayer($('#CartDrawer'));
  }

  async function addToCart(items) {
    const res = await fetch(`${routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    return data;
  }

  async function changeLine(line, quantity) {
    const res = await fetch(`${routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)) })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || 'Could not update cart');
    }
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-qty]');
    if (!btn) return;
    const drawer = $('#CartDrawer .drawer__panel');
    drawer && drawer.classList.add('is-loading');
    try {
      await changeLine(btn.dataset.cartQty, btn.dataset.qty);
      await refreshCartDrawer(false);
    } catch (err) {
      toast(err.message);
    } finally {
      drawer && drawer.classList.remove('is-loading');
    }
  });

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    btn.classList.add('is-loading');
    try {
      await addToCart([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]);
      await refreshCartDrawer(true);
    } catch (err) {
      toast(err.message);
    } finally {
      btn.classList.remove('is-loading');
    }
  });

  /* ---------- Product forms & variant selection ---------- */
  function initProduct(root) {
    if (!root || root.dataset.productReady) return;
    root.dataset.productReady = 'true';
    const form = $('[data-product-form]', root);
    const jsonEl = $('[data-product-json]', root);
    if (!form || !jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const idInput = $('[data-variant-id]', form);
    const addBtn = $('[data-add-to-cart]', form);
    const qtyInput = $('[data-qty-input]', form);
    const isMainProduct = !root.closest('#QuickView');

    const selectedOptions = () => $$('[data-option-index]:checked', form)
      .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
      .map((i) => i.value);

    const markAvailability = () => {
      const selected = selectedOptions();
      $$('[data-option-index]', form).forEach((input) => {
        const idx = Number(input.dataset.optionIndex);
        const probe = selected.slice();
        probe[idx] = input.value;
        const match = product.variants.find((v) => v.options.every((o, i) => o === probe[i]));
        input.closest('.swatch').classList.toggle('is-unavailable', !match || !match.available);
      });
    };

    const onVariantChange = () => {
      const opts = selectedOptions();
      opts.forEach((val, i) => { const label = $(`[data-option-value="${i}"]`, form); if (label) label.textContent = val; });
      const variant = product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      markAvailability();
      if (!variant) {
        addBtn.disabled = true;
        addBtn.textContent = window.theme.strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;

      const priceWrap = $('[data-product-price] [data-price]', root);
      if (priceWrap) {
        const onSale = variant.compare_at_price > variant.price;
        const pct = onSale ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
        priceWrap.classList.toggle('price--sale', onSale);
        priceWrap.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s><span class="price__badge">-${pct}%</span>` : '');
      }
      const sticky = $('[data-sticky-price]');
      if (sticky && isMainProduct) sticky.textContent = formatMoney(variant.price);
      const stickyBtn = $('[data-sticky-atc-btn]');
      if (stickyBtn && isMainProduct) stickyBtn.disabled = !variant.available;
      const sku = $('[data-variant-sku]', root);
      if (sku) sku.textContent = variant.sku || '';

      if (variant.featured_media && isMainProduct) showMedia(root, variant.featured_media.id);
      if (isMainProduct) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    form.addEventListener('change', (e) => { if (e.target.matches('[data-option-index]')) onVariantChange(); });
    markAvailability();

    form.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step || !qtyInput) return;
      qtyInput.value = Math.max(1, (Number(qtyInput.value) || 1) + Number(step.dataset.qtyStep));
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      addBtn.classList.add('is-loading');
      try {
        await addToCart([{ id: Number(idInput.value), quantity: Number(qtyInput ? qtyInput.value : 1) || 1 }]);
        closeLayer($('#QuickView'));
        await refreshCartDrawer(true);
      } catch (err) {
        toast(err.message);
      } finally {
        addBtn.classList.remove('is-loading');
      }
    });
  }

  function showMedia(root, mediaId) {
    const target = $(`.gallery__slide[data-media-id="${mediaId}"]`, root);
    if (!target) return;
    $$('.gallery__slide', root).forEach((s) => s.classList.toggle('is-active', s === target));
    $$('[data-thumb]', root).forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(mediaId)));
  }

  $$('[data-product-section]').forEach((root) => {
    initProduct(root);
    root.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-thumb]');
      if (thumb) showMedia(root, thumb.dataset.thumb);
    });
    // Swipe gallery on touch
    const main = $('[data-gallery-main]', root);
    if (main) {
      let sx = null;
      main.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx;
        const slides = $$('.gallery__slide', main);
        const idx = slides.findIndex((s) => s.classList.contains('is-active'));
        if (Math.abs(dx) > 40 && slides.length > 1) {
          const next = slides[(idx + (dx < 0 ? 1 : -1) + slides.length) % slides.length];
          showMedia(root, next.dataset.mediaId);
        }
        sx = null;
      });
    }
  });

  /* Hover zoom on desktop */
  $$('[data-zoom]').forEach((zoom) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    zoom.addEventListener('mouseenter', () => zoom.classList.add('is-zooming'));
    zoom.addEventListener('mouseleave', () => zoom.classList.remove('is-zooming'));
    const img = zoom.querySelector('img');
    zoom.addEventListener('mousemove', (e) => {
      if (!img) return;
      const r = zoom.getBoundingClientRect();
      img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
    });
  });

  /* Sticky add-to-cart */
  const stickyAtc = $('[data-sticky-atc]');
  const mainAtc = $('[data-product-section] [data-add-to-cart]');
  if (stickyAtc && mainAtc && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      stickyAtc.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }).observe(mainAtc);
    $('[data-sticky-atc-btn]', stickyAtc).addEventListener('click', () => mainAtc.click());
  }

  /* Copy link */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-copy-link]')) return;
    navigator.clipboard && navigator.clipboard.writeText(window.location.href).then(() => toast('Link copied'));
  });

  /* ---------- Quick view ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    const modal = $('#QuickView');
    const content = $('[data-quick-view-content]', modal);
    content.innerHTML = '<div class="qv"><p class="muted">Loading…</p></div>';
    openLayer(modal);
    try {
      const res = await fetch(`${routes.root}products/${btn.dataset.quickView}?view=quick`);
      content.innerHTML = await res.text();
      initProduct($('[data-product-section]', content));
    } catch (err) {
      content.innerHTML = '<div class="qv"><p>Could not load product.</p></div>';
    }
  });

  /* ---------- Wishlist (per-browser) ---------- */
  const WISHLIST_KEY = 'wc-wishlist';
  const getWishlist = () => store.get(WISHLIST_KEY, []);
  function syncWishlistUI() {
    const list = getWishlist();
    $$('[data-wishlist-toggle]').forEach((b) => b.classList.toggle('is-active', list.includes(b.dataset.wishlistToggle)));
    $$('[data-wishlist-count]').forEach((el) => { el.textContent = list.length; el.hidden = list.length === 0; });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.wishlistToggle;
    let list = getWishlist();
    if (list.includes(handle)) {
      list = list.filter((h) => h !== handle);
      toast('Removed from wishlist');
    } else {
      list.unshift(handle);
      toast('Added to wishlist');
    }
    store.set(WISHLIST_KEY, list);
    syncWishlistUI();
  });
  syncWishlistUI();

  async function renderCards(handles, grid) {
    const cards = await Promise.all(handles.map(async (h) => {
      try {
        const res = await fetch(`${routes.root}products/${h}?view=card`);
        return res.ok ? await res.text() : '';
      } catch (e) { return ''; }
    }));
    grid.innerHTML = cards.join('');
    fadeImages(grid);
    syncWishlistUI();
    return cards.filter(Boolean).length;
  }

  const wishlistPage = $('[data-wishlist-page]');
  if (wishlistPage) {
    const grid = $('[data-wishlist-grid]', wishlistPage);
    const empty = $('[data-wishlist-empty]', wishlistPage);
    const list = getWishlist();
    if (!list.length) empty.hidden = false;
    else renderCards(list, grid).then((n) => { if (!n) empty.hidden = false; });
  }

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'wc-recent';
  const productSection = $('main [data-product-section]');
  const currentHandle = productSection && productSection.dataset.productHandle;
  const recentSection = $('[data-recently-viewed]');
  if (recentSection) {
    const limit = Number(recentSection.dataset.limit) || 4;
    const handles = store.get(RECENT_KEY, []).filter((h) => h !== currentHandle).slice(0, limit);
    if (handles.length) {
      renderCards(handles, $('[data-recently-viewed-grid]', recentSection)).then((n) => { if (n) recentSection.hidden = false; });
    }
  }
  if (currentHandle) {
    const list = store.get(RECENT_KEY, []).filter((h) => h !== currentHandle);
    list.unshift(currentHandle);
    store.set(RECENT_KEY, list.slice(0, 12));
  }

  /* ---------- Product recommendations ---------- */
  const recos = $('[data-recommendations]');
  if (recos && !recos.children.length) {
    fetch(recos.dataset.url).then((r) => r.text()).then((text) => {
      const html = new DOMParser().parseFromString(text, 'text/html');
      const fresh = $('[data-recommendations]', html);
      if (fresh && fresh.innerHTML.trim()) { recos.innerHTML = fresh.innerHTML; syncWishlistUI(); }
    }).catch(() => {});
  }

  /* ---------- Predictive search ---------- */
  const searchInput = $('[data-predictive-input]');
  const searchResults = $('[data-predictive-results]');
  if (searchInput && searchResults) {
    let debounce;
    let controller;
    searchInput.addEventListener('input', () => {
      clearTimeout(debounce);
      const q = searchInput.value.trim();
      if (q.length < 2) { searchResults.innerHTML = ''; return; }
      debounce = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        try {
          const url = `${routes.search}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
          const data = await (await fetch(url, { signal: controller.signal })).json();
          const products = (data.resources && data.resources.results.products) || [];
          if (!products.length) {
            searchResults.innerHTML = `<p class="muted">No results for “${q.replace(/</g, '&lt;')}”.</p>`;
            return;
          }
          searchResults.innerHTML = products.map((p) => `
            <a href="${p.url}" class="predictive-item">
              ${p.image ? `<img src="${p.image}&width=120" alt="" loading="lazy" width="56" height="56">` : '<span></span>'}
              <span>${p.title}</span>
              <span class="predictive-item__price">${formatMoney(Math.round(Number(p.price) * 100))}</span>
            </a>`).join('') +
            `<a href="/search?type=product&q=${encodeURIComponent(q)}" class="link-arrow">View all results →</a>`;
        } catch (err) { /* aborted */ }
      }, 250);
    });
  }

  /* ---------- Collection filters & sorting (AJAX) ---------- */
  const collectionRoot = $('[data-collection]');
  if (collectionRoot) {
    const sectionEl = collectionRoot.closest('.shopify-section');
    const sectionId = sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
    const filterForm = $('[data-filter-form]', collectionRoot);
    const sortSelect = $('[data-sort-select]', collectionRoot);

    const buildParams = () => {
      const params = new URLSearchParams(new FormData(filterForm));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      params.set('sort_by', sortSelect.value);
      return params;
    };

    const render = async (params) => {
      const url = `${window.location.pathname}?${params.toString()}`;
      const results = $('[data-collection-results]', collectionRoot);
      results.classList.add('is-loading');
      try {
        const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${sectionId}`);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-collection]', html);
        if (fresh) {
          $('[data-collection-results]', collectionRoot).replaceWith($('[data-collection-results]', fresh));
          $('.filters__panel', collectionRoot).innerHTML = $('.filters__panel', fresh).innerHTML;
          $('.toolbar__count', collectionRoot).textContent = $('.toolbar__count', fresh).textContent;
        }
        window.history.replaceState({}, '', url);
        syncWishlistUI();
      } catch (e) {
        window.location.href = url;
      }
    };

    if (sectionId) {
      sortSelect.addEventListener('change', () => render(buildParams()));
      collectionRoot.addEventListener('change', (e) => {
        if (e.target.matches('[data-filter-input][type="checkbox"]') && window.matchMedia('(min-width: 990px)').matches) {
          render(buildParams());
        }
      });
      collectionRoot.addEventListener('submit', (e) => {
        if (!e.target.matches('[data-filter-form]')) return;
        e.preventDefault();
        render(buildParams());
        closeLayer($('#Filters'));
      });
    } else {
      sortSelect.addEventListener('change', () => { window.location.search = buildParams().toString(); });
    }
  }

  /* Footer menus start collapsed on phones */
  if (window.matchMedia('(max-width: 749px)').matches) {
    $$('.footer__acc[open]').forEach((d) => d.removeAttribute('open'));
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => { observeReveal(e.target); fadeImages(e.target); initMarquees(e.target); initBrandSliders(e.target); loadVendorCounts(e.target); syncWishlistUI(); });
})();
